import { useState } from 'react';
import { useValidateCreativeMutation } from 'services/api/creativeServer';
import { Option } from '../types';
import { TvServerEnvironment } from '../utils/tvEnvironment';

interface UseMosaicValidationProps {
  country: string | null;
  os: string | null;
  tvServerEnvironment?: TvServerEnvironment;
}

export interface UseMosaicValidationReturn {
  campaignID: string;
  creativeID: string;
  adslotState: boolean;
  creativeAdslots: Option[];
  validateError: string;
  progressview: boolean;
  progressStatus: React.ReactNode;
  handleCampaignIDChange: (value: string) => void;
  handleCreativeIDChange: (value: string) => void;
  handleValidateChange: () => Promise<void>;
}

export const useMosaicValidation = ({
  country,
  os,
  tvServerEnvironment,
}: UseMosaicValidationProps): UseMosaicValidationReturn => {
  const [campaignID, setCampaignID] = useState('');
  const [creativeID, setCreativeID] = useState('');
  const [adslotState, setAdslotState] = useState(false);
  const [creativeAdslots, setCreativeAdslots] = useState<Option[]>([]);
  const [validateError, setValidateError] = useState('');
  const [progressview, setProgressview] = useState(false);
  const [progressStatus, setProgressStatus] = useState<React.ReactNode>(
    'Validating Creative Metadata ...',
  );

  const [validateCreative] = useValidateCreativeMutation();

  const handleCampaignIDChange = (value: string) => {
    setCampaignID(value);
    setValidateError('');
    setCreativeAdslots([]);
    setAdslotState(false);
  };

  const handleCreativeIDChange = (value: string) => {
    setCreativeID(value);
    setValidateError('');
    setCreativeAdslots([]);
    setAdslotState(false);
  };

  const handleValidateChange = async () => {
    setAdslotState(false);
    setCreativeAdslots([]);
    setProgressview(true);
    setProgressStatus('Validating Creative Metadata ...');
    try {
      const validateCheck = await validateCreative({
        campaignId: campaignID,
        creativeId: creativeID,
        country,
        os,
        tvServerEnvironment,
      }).unwrap();
      if (validateCheck?.message) {
        setProgressview(false);
        setValidateError(validateCheck?.message);
        setAdslotState(false);
      }
      if (validateCheck?.adslots) {
        const adslotOptions = validateCheck.adslots.map((adslot: string) => ({
          key: adslot,
          value: adslot,
          label: adslot,
        }));
        setCreativeAdslots(adslotOptions);
        setValidateError('');
        setProgressview(false);
        setAdslotState(true);
      }
    } catch (error: any) {
      setProgressview(false);

      const apiMessage =
        error?.data?.message ||
        error?.error ||
        'Validation failed. Unknown error';

      setValidateError(apiMessage);
      setAdslotState(false);
    }
  };

  return {
    campaignID,
    creativeID,
    adslotState,
    creativeAdslots,
    validateError,
    progressview,
    progressStatus,
    handleCampaignIDChange,
    handleCreativeIDChange,
    handleValidateChange,
  };
};
